import React, { useEffect, useState } from 'react';
import './BannerPopup.css';

const FALLBACK_BILLBOARD_IMAGE = 'http://www.f-poster.ru/useruploads/images/naruzhnaya_bannernaya_reklama.JPG';
const yandexPreviewCache = new Map();

const isDirectImageUrl = (url) => {
  if (!url) return false;
  return /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(url);
};

const resolveYandexDiskImageUrl = async (publicKey) => {
  const cacheKey = String(publicKey || '').trim();
  if (!cacheKey) return null;
  if (yandexPreviewCache.has(cacheKey)) {
    return yandexPreviewCache.get(cacheKey);
  }

  const metaResponse = await fetch(
    `https://cloud-api.yandex.net/v1/disk/public/resources?public_key=${encodeURIComponent(cacheKey)}&preview_size=XL`,
  );
  if (metaResponse.ok) {
    const meta = await metaResponse.json();
    const preview =
      meta?.preview ||
      meta?.sizes?.find?.((s) => s?.url)?.url ||
      null;
    if (preview) {
      yandexPreviewCache.set(cacheKey, preview);
      return preview;
    }
  }

  const downloadResponse = await fetch(
    `https://cloud-api.yandex.net/v1/disk/public/resources/download?public_key=${encodeURIComponent(cacheKey)}`,
  );
  if (!downloadResponse.ok) {
    throw new Error('Не удалось получить ссылку на фото');
  }
  const payload = await downloadResponse.json();
  const resolved = payload?.href || null;
  yandexPreviewCache.set(cacheKey, resolved);
  return resolved;
};

const ICON_PIN = `${process.env.PUBLIC_URL || ''}/icons/banner-popup/pin.svg`;
const ICON_PIN_HOVER = `${process.env.PUBLIC_URL || ''}/icons/banner-popup/pin_hover.svg`;
const ICON_TRASH = `${process.env.PUBLIC_URL || ''}/icons/banner-popup/trash.svg`;
const ICON_REMOVE= `${process.env.PUBLIC_URL || ''}/icons/banner-popup/remove.svg`;

const PinIcon = () => (
  <span className="bp-pin" aria-hidden="true">
    <img className="bp-icon bp-pin-default" src={ICON_PIN} alt="" width={16} height={16} draggable={false} />
    <img className="bp-icon bp-pin-hover" src={ICON_PIN_HOVER} alt="" width={16} height={16} draggable={false} />
  </span>
);

const SwapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 11.5v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M8 11.5V5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M12.5 11.5V3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <img className="bp-icon" src={ICON_TRASH} alt="" width={16} height={16} draggable={false} />
);

const RestoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4.5 7.2 3 5.7 4.5 4.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.2 5.7h5.1a4.2 4.2 0 1 1 0 8.4H5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const BillboardPhoto = ({ photoLink }) => {
  const [resolvedSrc, setResolvedSrc] = useState('');
  const [linkHref, setLinkHref] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const run = async () => {
      const raw = typeof photoLink === 'string' ? photoLink.trim() : '';
      setLinkHref(raw || '');
      setIsLoading(true);
      setResolvedSrc('');

      if (!raw) {
        if (!isCancelled) setResolvedSrc(FALLBACK_BILLBOARD_IMAGE);
        return;
      }

      if (isDirectImageUrl(raw)) {
        if (!isCancelled) setResolvedSrc(raw);
        return;
      }

      if (/^(https?:\/\/)?(disk\.yandex\.ru|yadi\.sk)\//i.test(raw)) {
        try {
          const yandexSrc = await resolveYandexDiskImageUrl(raw);
          if (!isCancelled && yandexSrc) {
            setResolvedSrc(yandexSrc);
            return;
          }
        } catch {
          if (!isCancelled) setResolvedSrc(FALLBACK_BILLBOARD_IMAGE);
          return;
        }
      }

      if (!isCancelled) setResolvedSrc(FALLBACK_BILLBOARD_IMAGE);
    };

    run();
    return () => {
      isCancelled = true;
    };
  }, [photoLink]);

  const content = (
    <>
      {isLoading && (
        <div className="bp-photo-loader">
          <div className="bp-spinner" />
        </div>
      )}
      {resolvedSrc && (
        <img
          src={resolvedSrc}
          alt="Фото щита"
          referrerPolicy="no-referrer"
          onLoad={() => setIsLoading(false)}
          onError={(e) => {
            if (e.currentTarget.src !== FALLBACK_BILLBOARD_IMAGE) {
              setResolvedSrc(FALLBACK_BILLBOARD_IMAGE);
              return;
            }
            setIsLoading(false);
          }}
        />
      )}
    </>
  );

  if (!linkHref) {
    return <div className="bp-photo">{content}</div>;
  }

  return (
    <a
      className="bp-photo"
      href={linkHref}
      target="_blank"
      rel="noopener noreferrer"
      title="Открыть фото"
    >
      {content}
    </a>
  );
};

const isSelectedLabel = (actionLabel) => (
  actionLabel === 'MarkerIconBlueBanner'
  || actionLabel === 'MarkerIconGreenBanner'
);

export const BannerPopup = ({
  id = 'not_found',
  address = '',
  onShowTraffic,
  trafficActive = false,
  action,
  actionLabel,
  onDelete,
  onRestore,
  isDeleted = false,
  photoLink,
  mapLink,
  price,
}) => {
  const deleted = Boolean(isDeleted) || actionLabel === 'MarkerIconRedBanner';
  const selected = !deleted && isSelectedLabel(actionLabel);
  const primaryLabel = selected ? 'Убрать' : 'Выбрать';
  const addressText = String(address || '').trim() || '—';
  const priceText = price == null || price === '' ? '—' : String(price);
  const restoreAction = onRestore || (deleted ? action : null);

  const stopMapClick = (event) => {
    event.stopPropagation();
    if (event.nativeEvent?.stopImmediatePropagation) {
      event.nativeEvent.stopImmediatePropagation();
    }
  };

  return (
    <div
      className="bp-card"
      onMouseDown={stopMapClick}
      onClick={stopMapClick}
    >
      <div className="bp-body">
        <BillboardPhoto photoLink={photoLink} />

        <div className="bp-meta">
          <div className="bp-identity">
            <div className="bp-id">{id}</div>
            {mapLink ? (
              <a
                className="bp-address"
                href={mapLink}
                target="_blank"
                rel="noopener noreferrer"
                title="Открыть на карте"
              >
                <PinIcon />
                <span>{addressText}</span>
              </a>
            ) : (
              <div className="bp-address">
                <PinIcon />
                <span>{addressText}</span>
              </div>
            )}
          </div>

          <div className="bp-price-block">
            <hr className="bp-divider" />
            <div className="bp-price">{priceText}</div>
          </div>

          <div className="bp-actions">
            {action && !deleted && (
              <button
                type="button"
                className={`bp-btn bp-btn-primary${selected ? ' is-selected' : ''}`}
                onClick={action}
              >
                {primaryLabel}
              </button>
            )}

            {onShowTraffic && (
              <button
                type="button"
                className={`bp-btn bp-btn-icon${trafficActive ? ' is-active' : ''}`}
                onClick={onShowTraffic}
                aria-label="Показать трафик"
                title="Показать трафик"
                aria-pressed={trafficActive ? 'true' : 'false'}
              >
                <SwapIcon />
              </button>
            )}

            {deleted && restoreAction ? (
              <button
                type="button"
                className="bp-btn bp-btn-restore"
                onMouseDown={stopMapClick}
                onClick={(event) => {
                  stopMapClick(event);
                  restoreAction(event);
                }}
                aria-label="Вернуть в последующие расчеты"
                title="Вернуть в последующие расчеты"
              >
                <RestoreIcon />
              </button>
            ) : onDelete ? (
              <button
                type="button"
                className="bp-btn bp-btn-danger"
                onMouseDown={stopMapClick}
                onClick={(event) => {
                  stopMapClick(event);
                  onDelete(event);
                }}
                aria-label="Удалить из последующих расчетов"
                title="Удалить из последующих расчетов"
              >
                <TrashIcon />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
